/**
 * BrandStoreContext — Global brand + store context for multi-brand/multi-store navigation.
 *
 * Provides:
 *   - activeBrandId: currently selected brand
 *   - activeStoreId: currently selected store within that brand
 *   - brands: list of brands for the user
 *   - stores: list of stores for the active brand
 *   - switchBrand(id): change active brand (resets store)
 *   - switchStore(id): change active store
 *
 * For single-brand/single-store users, everything auto-resolves silently.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../contexts';
import { resolveBrandStoreMarketDefaults } from '@bright-web-skeleton/shared';

interface BrandSummary {
  id: string;
  name: string;
  handle: string | null;
  avatar_url: string | null;
}

interface StoreSummary {
  id: string;
  display_name: string | null;
  slug: string | null;
  country_code: string;
  default_locale: string;
  supported_locales: readonly string[];
  time_zone: string;
  is_default: boolean;
  status: string;
}

interface BrandStoreContextValue {
  activeBrandId: string | null;
  activeStoreId: string | null;
  brands: BrandSummary[];
  stores: StoreSummary[];
  isMultiBrand: boolean;
  isMultiStore: boolean;
  loading: boolean;
  switchBrand: (brandId: string) => void;
  switchStore: (storeId: string) => void;
  refresh: () => Promise<void>;
}

const BrandStoreContext = createContext<BrandStoreContextValue>({
  activeBrandId: null,
  activeStoreId: null,
  brands: [],
  stores: [],
  isMultiBrand: false,
  isMultiStore: false,
  loading: true,
  switchBrand: () => {},
  switchStore: () => {},
  refresh: async () => {},
});

const STORAGE_KEY_BRAND = 'koordinator_active_brand';
const STORAGE_KEY_STORE = 'koordinator_active_store';

export const BrandStoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, authReady } = useAuth();
  const [brands, setBrands] = useState<BrandSummary[]>([]);
  const [stores, setStores] = useState<StoreSummary[]>([]);
  const [activeBrandId, setActiveBrandId] = useState<string | null>(null);
  const [activeStoreId, setActiveStoreId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  const loadBrands = useCallback(async () => {
    if (!user?.id) return;

    // Get all brands for this user
    const { data: brandUsers } = await supabase
      .from('brand_users')
      .select('brand_id')
      .eq('user_id', user.id);

    if (!brandUsers?.length) {
      // Fallback: check brands.user_id (legacy single-owner)
      const { data: ownedBrands } = await supabase
        .from('brands')
        .select('id, name, handle, avatar_url')
        .eq('user_id', user.id)
        .is('retired_at', null);

      setBrands(ownedBrands ?? []);
      return ownedBrands ?? [];
    }

    const brandIds = brandUsers.map((bu) => bu.brand_id);
    const { data: brandRows } = await supabase
      .from('brands')
      .select('id, name, handle, avatar_url')
      .in('id', brandIds)
      .is('retired_at', null);

    setBrands(brandRows ?? []);
    return brandRows ?? [];
  }, [user?.id]);

  const loadStores = useCallback(async (brandId: string) => {
    const { data: storeRows } = await supabase
      .from('brand_stores')
      .select('id, display_name, slug, country_code, is_default, status')
      .is('retired_at', null)
      .eq('brand_id', brandId)
      .order('is_default', { ascending: false });

    const storesWithMarketDefaults: StoreSummary[] = (storeRows ?? []).map((store) => {
      const market = resolveBrandStoreMarketDefaults(store.country_code);
      return {
        ...store,
        default_locale: market.defaultLocale,
        supported_locales: market.supportedLocales,
        time_zone: market.timeZone,
      };
    });

    // Rollout-safe: derive from country until the locale migration is applied.
    // The query intentionally does not request the new columns yet.
    setStores(storesWithMarketDefaults);
    return storesWithMarketDefaults;
  }, []);

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);

    try {
      const loadedBrands = await loadBrands();
      if (!loadedBrands?.length) {
        setLoading(false);
        return;
      }

      // Restore or pick brand
      const savedBrand = localStorage.getItem(STORAGE_KEY_BRAND);
      const validSaved = savedBrand && loadedBrands.some((b) => b.id === savedBrand);
      const brandId = validSaved ? savedBrand! : loadedBrands[0].id;
      setActiveBrandId(brandId);
      localStorage.setItem(STORAGE_KEY_BRAND, brandId);

      // Load stores for active brand
      const loadedStores = await loadStores(brandId);

      // Restore or pick store
      const savedStore = localStorage.getItem(STORAGE_KEY_STORE);
      const validStoreMatch = savedStore && loadedStores?.some((s) => s.id === savedStore);
      const defaultStore = loadedStores?.find((s) => s.is_default);
      const storeId = validStoreMatch
        ? savedStore!
        : (defaultStore?.id ?? loadedStores?.[0]?.id ?? null);
      setActiveStoreId(storeId);
      if (storeId) localStorage.setItem(STORAGE_KEY_STORE, storeId);
    } catch (err) {
      console.error('[BrandStoreContext] load error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, loadBrands, loadStores]);

  useEffect(() => {
    if (!authReady || !user?.id) return;
    if (loadedRef.current) return;
    loadedRef.current = true;
    refresh();
  }, [authReady, user?.id, refresh]);

  const switchBrand = useCallback(
    async (brandId: string) => {
      setActiveBrandId(brandId);
      localStorage.setItem(STORAGE_KEY_BRAND, brandId);

      // Reset store context
      const newStores = await loadStores(brandId);
      const defaultStore = newStores?.find((s) => s.is_default);
      const newStoreId = defaultStore?.id ?? newStores?.[0]?.id ?? null;
      setActiveStoreId(newStoreId);
      if (newStoreId) localStorage.setItem(STORAGE_KEY_STORE, newStoreId);
      else localStorage.removeItem(STORAGE_KEY_STORE);
    },
    [loadStores],
  );

  const switchStore = useCallback((storeId: string) => {
    setActiveStoreId(storeId);
    localStorage.setItem(STORAGE_KEY_STORE, storeId);
  }, []);

  return (
    <BrandStoreContext.Provider
      value={{
        activeBrandId,
        activeStoreId,
        brands,
        stores,
        isMultiBrand: brands.length > 1,
        isMultiStore: stores.length > 1,
        loading,
        switchBrand,
        switchStore,
        refresh,
      }}
    >
      {children}
    </BrandStoreContext.Provider>
  );
};

export function useBrandStoreContext() {
  return useContext(BrandStoreContext);
}
